import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import useProjectContext from "@/services/context/project/use-project-context";
import { useOptionaleProjectMeasureContext } from "@/pages/takeoff/context/project-measure/use-project-measure-context";

import useJobsAsync from "@/hooks/jobs/use-jobs-async";
import useJobAsync from "@/hooks/jobs/use-job-async";

import ENDPOINTS from "@/services/endpoints";
import MEASURE_ENDPOINTS from "@/services/endpoints/measure-endpoints.ts";
import { putItem } from "@/queries/queries-helper";

import PlanDetectionTaskDTO, { planDetectionTaskAdapter } from "@/models/interfaces/dtos/plan-detection/plan-detection-task-dto";
import JobDto, { prodTaskAdapter } from "@/models/interfaces/dtos/production/job-dto";

import useProjectMeasureSync from "./use-project-measure-sync";
import LaunchPlanDetectionOptions from "../layouts/overview/components/launch-plan-detection-options";
import FileRecord from "@/pages/takeoff/context/project-measure/models/file-record";
import { PlanDetectionConfigurationDTO } from "@/models/interfaces/dtos/plan-detection/plan-detection-options-dto";
import PlanDetectionProvider from "@/models/enums/plan-detection-provider";
import useValidationContext from "@/pages/takeoff/layouts/validation/context/validation/use-validation-context";

const useMeasureProjectActions = () => {
    const { project } = useProjectContext();

    const projectMeasureContext = useOptionaleProjectMeasureContext();
    const { refreshData: refreshValidationData } = useValidationContext();

    const {
        updateProjectSpaces,
        updateProjectOpenings,
        updateProjectWalls,
        updateProjectSlabs
    } = useProjectMeasureSync();

    const processNameRef = useRef<string | undefined>(undefined);

    const deleteExistingMeasuresRef = useRef<boolean>(false);

    const allJobsFinishedCallbackRef = useRef<(() => Promise<void> | void) | undefined>(undefined);

    const [searchParams, setSearchParams] = useSearchParams();

    const setupLaunch = useCallback((processName: string, deleteExistingMeasures: boolean, allJobsFinishedCallback?: () => Promise<void> | void) => {
        processNameRef.current = processName;
        deleteExistingMeasuresRef.current = deleteExistingMeasures;
        allJobsFinishedCallbackRef.current = allJobsFinishedCallback;
    }, []);

    const [launchRequest, setLaunchRequest] = useState<PlanDetectionConfigurationDTO | null>(null);
    const lastLaunchedRequestRef = useRef<typeof launchRequest>(null);
    const launchPlanDetectionProvider = launchRequest?.provider ?? PlanDetectionProvider.Wisebim;

    const planDetectionJobsData = useMemo(() => {
        const byUrl = new Map<
            string,
            {
                storeyId: string;
                file: FileRecord;
            }
        >();

        const byStoreyId = new Map<string, string>();

        projectMeasureContext?.storeysFileData.forEach((value, key) => {
            value.forEach(file => {
                const url = ENDPOINTS.PLAN_DETECTION_LAUNCH(
                    project.id,
                    key,
                    file.fileRevisionId,
                    launchPlanDetectionProvider
                );

                byUrl.set(url, {
                    storeyId: key,
                    file
                });

                byStoreyId.set(key, url);
            });
        });

        return {
            byUrl,
            byStoreyId
        };
    }, [project.id, projectMeasureContext, launchPlanDetectionProvider]);

    const storeyId = searchParams.get("storeyId");

    const planDetectionJobsUrls = useMemo(() => {
        if (!storeyId) return Array.from(planDetectionJobsData.byUrl.keys());

        const url = planDetectionJobsData.byStoreyId.get(storeyId);
        return url ? [url] : [];
    }, [storeyId, planDetectionJobsData]);

    // Lancement de plusieurs jobs via plan detection (wisebim / amplify) pour la détection en automatique ; soit depuis la vue d'ensemble (plusieurs plans) soit depuis la vue de saisie
    const { launchJobs: _launchPlanDetectionJobs, isPolling: isPollingPlanDetectionJobs } = useJobsAsync<PlanDetectionTaskDTO>({
        jobsUrl: planDetectionJobsUrls,
        jobBody: launchRequest,
        getStatusUrl: ENDPOINTS.PLAN_DETECTION_STATUS,
        adapter: planDetectionTaskAdapter,

        onJobDone: async (_job, _status, jobUrl, jobId) => {
            // NE PAS APPELER DES METHODES QUI POURRAIT REMONTER LE COMPOSANT COMME PAR EXEMPLE "await projectMeasureContext?.refreshData()"
            // SINON LE TOAST D'AVANCEMENT RISQUE DE NE PAS SE FERMER

            const entry = planDetectionJobsData.byUrl.get(jobUrl)!;

            await putItem(
                MEASURE_ENDPOINTS.UPDATE_STOREY_MEASURES_FROM_DETECTION(
                    project.id,
                    entry.storeyId,
                    entry.file.fileRevisionId,
                    jobId
                ),
                // New jobs import with their server-side configuration snapshot.
                launchRequest
            );
        },

        allJobsFinished: async () => {
            processNameRef.current = undefined;

            if (allJobsFinishedCallbackRef.current) {
                const callback = allJobsFinishedCallbackRef.current;
                allJobsFinishedCallbackRef.current = undefined;
                await callback();
                // return;
            }

            searchParams.delete("launchPlanDetectionJobs");
            setSearchParams(searchParams);

            await projectMeasureContext?.invalidateMeasureTypes();
            await projectMeasureContext?.refreshData();
        },

        jobStatusMessage: {
            pending: (current, total) => `${total > 1 ? `Détection automatique Etage ${current} sur ${total} en cours...` : 'Détection automatique en cours...'}`
        }
    });

    useEffect(() => {
        if (!launchRequest || lastLaunchedRequestRef.current === launchRequest) return;

        // Attendre le rendu contenant les URL du fournisseur confirmé.
        // Consommer la demande avant le lancement pour éviter les doublons.
        lastLaunchedRequestRef.current = launchRequest;
        _launchPlanDetectionJobs();
    }, [launchRequest, _launchPlanDetectionJobs]);

    // Lancement d'un job via prodAPI
    const { launchJob: _launchProdJob, isLoading: isLoadingProdJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(
            project.id,
            processNameRef.current!,
            deleteExistingMeasuresRef.current
        ),
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(project.id),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,

        jobFinished: async (succeeded, isExistingJob) => {
            const processName = processNameRef.current;
            processNameRef.current = undefined;
            const callback = allJobsFinishedCallbackRef.current;
            allJobsFinishedCallbackRef.current = undefined;

            if (succeeded && !isExistingJob && (
                processName === "PDF Superposes - Calcul Package 1&2" ||
                processName === "PDF Superposes - Update Package 1&2"
            )) {
                try {
                    // Rafraîchir avant le callback, qui peut naviguer vers les métrés.
                    await refreshValidationData();
                } catch (error) {
                    toast.error(`Le calcul a réussi, mais le rafraîchissement des validations a échoué : ${error}`);
                }
            }

            // Un job repris peut correspondre à un autre calcul du même projet.
            if (succeeded && !isExistingJob && callback) {
                try {
                    await callback();
                } catch (error) {
                    toast.error(`Le calcul a réussi, mais la mise à jour du statut a échoué : ${error}`);
                }
            }

            await projectMeasureContext?.invalidateMeasureTypes();
            await projectMeasureContext?.refreshData();
        },
        onError: () => {
            processNameRef.current = undefined;
            allJobsFinishedCallbackRef.current = undefined;
        },
    });

    useEffect(() => {
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!isPollingPlanDetectionJobs) return;

            event.preventDefault();
        };

        window.addEventListener("beforeunload", onBeforeUnload);

        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [isPollingPlanDetectionJobs]);

    const [isCalculatingSurfaces, setIsCalculatingSurfaces] = useState(false);

    const updateProjectSurfaces = useCallback(async (): Promise<void> => {
        setIsCalculatingSurfaces(true);
        const toastId = toast.loading("Mise à jour du projet...");

        try {
            await updateProjectWalls.mutateAsync(project.id);
            await updateProjectOpenings.mutateAsync(project.id);
            await updateProjectSpaces.mutateAsync(project.id);
            await updateProjectSlabs.mutateAsync(project.id);

            toast.update(toastId, {
                render: "La mise à jour du projet est terminée.",
                type: "success",
                isLoading: false,
                autoClose: 5000
            });
        } catch (error) {
            processNameRef.current = undefined;
            allJobsFinishedCallbackRef.current = undefined;
            toast.update(toastId, {
                render: `Erreur: ${error}`,
                type: "error",
                isLoading: false,
                autoClose: false,
                closeButton: true
            });
            throw error;
        } finally {
            setIsCalculatingSurfaces(false);
        }
    }, [project.id, updateProjectWalls, updateProjectOpenings, updateProjectSpaces, updateProjectSlabs]);

    // const [showWarning, setShowWarning] = useState(false);
    const [showOptions, setShowOptions] = useState(false);

    const launchPlanDetectionJobs = useCallback(() => {
        // setShowWarning(true);
        setShowOptions(true);
    }, []);

    const launchProdJob = useCallback(() => {
        _launchProdJob();
    }, [_launchProdJob]);

    // const confirmWarning = useCallback(() => {
    //     setShowWarning(false);
    //     setShowOptions(true);
    // }, []);

    // const cancelWarning = useCallback(() => {
    //     setShowWarning(false);

    //     searchParams.delete("launchPlanDetectionJobs");
    //     setSearchParams(searchParams);
    // }, [searchParams, setSearchParams]);

    const confirmOptions = useCallback((configuration: PlanDetectionConfigurationDTO) => {
        setLaunchRequest(configuration);
    }, []);

    // useEffect(() => {
    //     if (searchParams.get("launchPlanDetectionJobs")?.toLowerCase() === "true") {
    //         setShowWarning(true);
    //     }
    // }, [searchParams]);

    const dialogs = useMemo(() => (
        <>
            {/* <WarningDialog
                title="Confirmer l'action"
                message="Les données actuelles seront écrasées par les nouvelles."
                open={showWarning}
                onCancel={cancelWarning}
                onConfirm={confirmWarning}
            /> */}

            <LaunchPlanDetectionOptions
                key={project.id}
                projectId={project.id}
                dialogOpen={showOptions}
                setDialogOpen={setShowOptions}
                handleConfirm={confirmOptions}
            />
        </>
    ), [showOptions, confirmOptions, project.id]);

    return useMemo(() => ({
        setupLaunch,
        launchPlanDetectionJobs,
        updateProjectSurfaces,
        launchProdJob,

        isPollingPlanDetectionJobs,
        isLoadingProdJob,
        isCalculatingSurfaces,

        dialogs
    }), [
        setupLaunch,
        launchPlanDetectionJobs,
        updateProjectSurfaces,
        launchProdJob,
        isPollingPlanDetectionJobs,
        isLoadingProdJob,
        isCalculatingSurfaces,
        dialogs
    ]);
}

export default useMeasureProjectActions;
